import { type FC, useRef } from 'react';

import { VideoCameraIcon } from '@heroicons/react/24/outline';

import type { VideoUploadLimits } from '@/types';
import { VIDEO_UPLOAD_ACCEPT } from '@/video/constants';
import { formatVideoLimits } from '@/video/formatVideoLimits';

import { ICON_CLASS, tr } from '../constants';
import { useVideoDrop } from '../useVideoDrop';
import { VideoDropOverlay } from '../VideoDropOverlay';
import { PanelPopover } from './PanelPopover';

interface VideoPanelProps {
  // Hands a picked or dropped file to the composer, which validates it, inserts the
  // placeholder, and closes this popover. The placeholder says why an invalid file
  // was rejected.
  onSelectFile: (file: File) => void;
  limits: VideoUploadLimits;
  locale?: string;
  translations?: Record<string, string>;
  accept?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const VideoPanelBody: FC<Omit<VideoPanelProps, 'open' | 'onOpenChange'>> = ({
  onSelectFile,
  limits,
  locale,
  translations,
  accept,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { size, duration } = formatVideoLimits(limits, locale);

  // Portal events bubble through the React composer tree, so stop propagation to keep a
  // drop here from also triggering the composer-level drop and starting the upload twice.
  const { isDragActive, dropHandlers } = useVideoDrop({ onFile: onSelectFile, stopPropagation: true });

  return (
    <div {...dropHandlers} className="relative flex flex-col items-center gap-3 p-6">
      {isDragActive && <VideoDropOverlay translations={translations} limits={limits} locale={locale} />}
      <input
        ref={inputRef}
        type="file"
        accept={accept ?? VIDEO_UPLOAD_ACCEPT}
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) onSelectFile(file);
          event.target.value = '';
        }}
      />

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="inline-flex h-10 items-center justify-center rounded-full bg-black px-5 text-[13px] font-medium leading-4 tracking-[-0.13px] text-white hover:bg-black/90"
      >
        {tr(translations, 'video.choose_file')}
      </button>
      <p className="text-center text-xs text-black/50">
        {tr(translations, 'video.hint').replace('{{size}}', size).replace('{{duration}}', duration)}
      </p>
    </div>
  );
};

export const VideoPanel: FC<VideoPanelProps> = ({
  onSelectFile,
  limits,
  locale,
  translations,
  accept,
  open,
  onOpenChange,
}) => (
  <PanelPopover
    triggerLabel={tr(translations, 'community.composer.add_video')}
    triggerIcon={<VideoCameraIcon className={ICON_CLASS} aria-hidden="true" />}
    panelClassName="w-80"
    open={open}
    onOpenChange={onOpenChange}
  >
    {() => (
      <VideoPanelBody
        onSelectFile={onSelectFile}
        limits={limits}
        locale={locale}
        translations={translations}
        accept={accept}
      />
    )}
  </PanelPopover>
);
